import React, { useCallback } from "react";
import { ScrollView } from "react-native";
import { useI18n } from "../../hooks/i18n-hooks";
import { useMutation } from "@apollo/client";
import { AuthManagerContext } from "../../contexts/AuthManager";
import VStack from "../../ui_atoms/VStack";
import ScreenCard from "../ScreenCard";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useCloseEditor } from "../profile-editor/use-close-editor";
import { DocumentType, Document } from "../../__generated__/graphql";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { UPDATE_USER } from "../../graphql/identity";
import isEmail from "validator/lib/isEmail";
import TextField from "../../components/TextField";
import { usePostOnboardingData } from "../../hooks/use-postonboarding-data";
import { useIntercom } from "../../hooks/intercom";

type FormData = {
    email?: string;
};

type PrivateEmailEditorScreenProps = StackScreenProps<RootStackParamList, "PrivateEmailEdit">;

const PrivateEmailEditorScreen = ({ navigation, route }: PrivateEmailEditorScreenProps) => {
    const { profileId } = route.params || {};
    const { email } = usePostOnboardingData();
    const { refreshUser } = React.useContext(AuthManagerContext);
    const { updateUser: updateIntercomUser } = useIntercom();
    const [updateUser, { error: saveError, loading: saving, reset }] = useMutation(
        UPDATE_USER,
        profileId
            ? {
                  context: { liftProfile: profileId },
              }
            : undefined,
    );

    useApolloErrorSnackbar(saveError, reset);

    const i18n = useI18n();

    const {
        handleSubmit,
        control,
        formState: { isValid, isDirty },
    } = useForm<FormData>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            email: email || undefined,
        },
    });

    const onSubmit = useCallback<SubmitHandler<FormData>>(
        async (data) => {
            try {
                await updateUser({ variables: { userData: { email: data.email } } });
                navigation.goBack();
                void refreshUser?.();
                // We update the intercom user to use the private email
                void updateIntercomUser({ email: data.email });
            } catch (ex) {
                // console.error(ex);
                // this is handled by updateError in useMutation
            }
        },
        [updateUser, updateIntercomUser, navigation, refreshUser],
    );

    useCloseEditor(isDirty && !saving);

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("save"),
                    onPress: handleSubmit(onSubmit),
                    validating: saving,
                    isValid: isValid && isDirty,
                    mode: "secondary",
                },
            ]}
        >
            <ScrollView>
                <VStack v={1} gap={0.5} alignment="stretch" h="2/3">
                    <VStack gap={0.25}>
                        <Controller
                            control={control}
                            name="email"
                            rules={{
                                validate: (v = "") => isEmail(v),
                            }}
                            render={({
                                field: { onChange, onBlur, value },
                                fieldState: { error },
                            }) => (
                                <TextField
                                    label={i18n.t("screens.profile_editor.private_email.title")}
                                    value={value}
                                    onChangeText={onChange}
                                    onBlur={onBlur}
                                    error={error?.message}
                                />
                            )}
                        />
                    </VStack>
                </VStack>
            </ScrollView>
        </ScreenCard>
    );
};

export default PrivateEmailEditorScreen;
